import { useEffect, useMemo, useState } from "react";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import {
  BotonExportarExcel,
  Button,
  Card,
  Group,
  Input,
  PaginadorTabla,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { CentroDeCostoConOrganizaciones } from "@/lib/types";
import {
  importarCentrosDeCosto,
  listarCentrosDeCostoConOrganizaciones,
} from "../services/catalogos-contables-admin-service";
import { useCecosConOrgsAdmin } from "../hooks/use-catalogos-contables-admin";
import { useCrearCeco, useEditarCeco, useImportarCecos } from "../hooks/use-catalogos-contables-mutations";
import { CecoConOrgsRow } from "./CecoConOrgsRow";
import { CecoCrearForm } from "./CecoCrearForm";
import { ImportarExcelCatalogo } from "./ImportarExcelCatalogo";

const PAGE_SIZE = 15;

const EJEMPLOS_CECOS = [
  { codigo: "7220061800", nombre: "Tesorería (ejemplo · reemplázame)", numeroSociedad: "5200" },
  // Un mismo código puede repetirse en varias sociedades (relación M:N).
  { codigo: "CC-001", nombre: "Administración (ejemplo · reemplázame)", numeroSociedad: "5200" },
  { codigo: "CC-001", nombre: "Administración (ejemplo · reemplázame)", numeroSociedad: "5300" },
];

/**
 * Gestor de CeCo + sus RELACIONES con organizaciones (M:N) · búsqueda + paginado
 * server-side. Al crear un CeCo es obligatorio elegir al menos una organización;
 * la columna Organizaciones muestra a cuáles pertenece y el editar las sincroniza.
 */
export function SeccionCecos() {
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const serverParams = useMemo(
    () => ({
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [busquedaDebounced, page]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useCecosConOrgsAdmin(serverParams);
  const cecos = data?.items ?? [];
  const total = data?.total ?? 0;

  const [modoCrear, setModoCrear] = useState(false);
  const crear = useCrearCeco({ onCreado: () => setModoCrear(false) });
  const editar = useEditarCeco();
  const importar = useImportarCecos();

  const obtenerTodos = async (): Promise<CentroDeCostoConOrganizaciones[]> => {
    const b = busquedaDebounced.trim();
    const todos: CentroDeCostoConOrganizaciones[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarCentrosDeCostoConOrganizaciones({ ...(b ? { busqueda: b } : {}), page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Input placeholder="Buscar por código, nombre u organización…" leftSection={<IconSearch size={14} stroke={1.7} />} value={busqueda} onChange={(e) => setBusqueda(e.currentTarget.value)} wrapperClassName="w-80" />
        <Group gap="xs">
          <ImportarExcelCatalogo importar={importar} previsualizar={(filas) => importarCentrosDeCosto(filas, true)} plantillaArchivo="plantilla-centros-de-costo" plantillaHoja="Centros de costo" ejemplos={EJEMPLOS_CECOS} incluyeSociedad />
          {!modoCrear ? (
            <Button leftSection={<IconPlus size={14} />} onClick={() => setModoCrear(true)}>
              Nuevo CeCo
            </Button>
          ) : null}
        </Group>
      </Group>
      {modoCrear ? (
        <CecoCrearForm loading={crear.isPending} onCancelar={() => setModoCrear(false)} onCrear={crear.mutate} />
      ) : null}
      <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
      <Card padding="lg">
        <Group justify="flex-end" className="mb-2">
          <BotonExportarExcel
            archivo="centros-de-costo"
            hoja="Centros de costo"
            obtenerFilas={obtenerTodos}
            columnas={[
              { header: "Código", value: (c) => c.codigo },
              { header: "Nombre", value: (c) => c.nombre },
              { header: "Organizaciones", value: (c) => c.organizaciones.map((o) => `${o.numeroSociedad} · ${o.nombre}`).join(" | ") },
            ]}
          />
        </Group>
        <div className="overflow-x-auto">
          <Table verticalSpacing="sm">
            <Thead>
              <Tr>
                <Th>Código</Th>
                <Th>Nombre</Th>
                <Th>Organizaciones</Th>
                <Th />
              </Tr>
            </Thead>
            {!data ? (
              <TablaSkeleton dentroDeTabla columnas={[{}, {}, {}, { align: "right" }]} filas={PAGE_SIZE} />
            ) : (
              <Tbody>
                {cecos.map((ceco) => (
                  <CecoConOrgsRow key={ceco.id} ceco={ceco} onGuardar={editar.mutate} isPending={editar.isPending} />
                ))}
              </Tbody>
            )}
          </Table>
        </div>
      </Card>
      <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="CeCos" />
    </Stack>
  );
}
